import { addYears, format, isValid, isWithinInterval, startOfDay, subDays } from 'date-fns';

import { forceLocalDate } from '../../utils/dates';
import { ForecastWindowSpec } from '../dashboard/types';
import { getCustomYearsWindowSpec } from './get-forecast-window-spec';

type CustomYearsWindowSpec = Extract<ForecastWindowSpec, { kind: 'custom-years' }>;

export interface FixedForecastWindowPayload {
  forecast_from_date: string;
  window_from: string;
  window_spec: CustomYearsWindowSpec;
}

interface GetFixedForecastWindowPayloadParams {
  duration: string;
  windowFrom: string;
}

interface GetForecastFromDateWithinFixedWindowParams {
  duration: string;
  forecastFromDate: string | null | undefined;
  windowFrom: string;
}

/**
 * Keeps an existing forecast start inside a custom-years window.
 *
 * @param params - Existing forecast start and amended fixed-window values.
 * @param params.duration - Number of years selected for the amended forecast.
 * @param params.forecastFromDate - Existing forecast start formatted as `yyyy-MM-dd`.
 * @param params.windowFrom - Edited window start formatted as `yyyy-MM-dd`.
 * @returns The existing forecast start when it is inside the amended window; otherwise the window start.
 */
export const getForecastFromDateWithinFixedWindow = ({
  duration,
  forecastFromDate,
  windowFrom
}: GetForecastFromDateWithinFixedWindowParams): string => {
  if (!forecastFromDate) {
    return windowFrom;
  }

  const durationYears = Number(duration);
  const forecastFrom = startOfDay(forceLocalDate(forecastFromDate));
  const windowFromDate = startOfDay(forceLocalDate(windowFrom));
  const windowLastDate = subDays(addYears(windowFromDate, durationYears), 1);

  if (
    !Number.isFinite(durationYears) ||
    durationYears <= 0 ||
    !isValid(forecastFrom) ||
    !isValid(windowFromDate) ||
    !isValid(windowLastDate) ||
    windowFromDate.getTime() > windowLastDate.getTime()
  ) {
    return windowFrom;
  }

  return isWithinInterval(forecastFrom, { start: windowFromDate, end: windowLastDate })
    ? forecastFromDate
    : windowFrom;
};

/**
 * Builds a fixed forecast window and keeps `forecast_from_date` within its inclusive boundaries.
 *
 * @param params - Values selected in the forecast form.
 * @param params.duration - Number of years selected for the forecast.
 * @param params.windowFrom - Selected Start date formatted as `yyyy-MM-dd`.
 * @returns The calculated forecast start date and custom-year window fields.
 */
export const getFixedForecastWindowPayload = ({
  duration,
  windowFrom
}: GetFixedForecastWindowPayloadParams): FixedForecastWindowPayload => {
  const durationYears = Number(duration);
  const today = startOfDay(new Date());
  const todayDate = format(today, 'yyyy-MM-dd');
  const windowFromDate = startOfDay(forceLocalDate(windowFrom));
  // New forecasts have no API-provided `window_till` yet, so creation still derives this boundary locally.
  const windowTill = addYears(windowFromDate, durationYears);
  const forecastFromDate = isWithinInterval(today, { start: windowFromDate, end: windowTill })
    ? todayDate
    : windowFrom;

  return {
    forecast_from_date: forecastFromDate,
    window_from: windowFrom,
    window_spec: getCustomYearsWindowSpec(duration)
  };
};
